import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { toast } from "sonner";
import { isBillingInterval, isSelfServePlan } from "@headerhawk/core/plans";
import {
  api,
  ApiError,
  type BillingInterval,
  type Plan,
  type CheckoutSubscriptionInfo,
} from "@/lib/api/client";

/**
 * Returning from Stripe Checkout, verified.
 *
 * There are two places a user can come back from Stripe - onboarding and
 * billing settings - and they used to disagree about what "success" meant.
 * Onboarding round-tripped the session ID and asked the API. Billing trusted a
 * `?upgraded=true` query parameter and fired a success toast immediately, so it
 * would announce "Subscription upgraded successfully!" while the plan shown
 * directly below it was still the old one (the webhook had not landed yet) -
 * and would do the same for anyone who simply pasted the URL (FE-M2, task 163).
 *
 * This hook is the single implementation. A return is only a success once
 * `/checkout/verify` says the subscription is active or trialing on a non-free
 * plan; anything else is reported honestly and the query parameter is cleared
 * either way.
 */

/**
 * Build the Stripe redirect URLs for a checkout that returns to `path`.
 *
 * `{CHECKOUT_SESSION_ID}` is a Stripe placeholder - it substitutes the real
 * session ID when it redirects. Without it there is nothing to verify against,
 * which is how the billing page ended up trusting a bare boolean.
 */
export function checkoutReturnUrls(path: string): {
  successUrl: string;
  cancelUrl: string;
} {
  const base = `${window.location.origin}${path}`;
  return {
    successUrl: `${base}?session_id={CHECKOUT_SESSION_ID}`,
    cancelUrl: `${base}?cancelled=true`,
  };
}

export type CheckoutStartResult =
  /** The browser is on its way to Stripe. Nothing else will render. */
  | { status: "redirecting" }
  /**
   * The trial was refused because this account has already used one. Not a
   * failure: paid checkout is still available, by calling again with
   * `allowTrial: false`.
   */
  | { status: "trial_not_eligible"; message: string };

/**
 * Start Stripe Checkout and redirect, shared by onboarding and billing settings.
 *
 * Both call sites build the return URLs the same way - with
 * `{CHECKOUT_SESSION_ID}` - and handle a refused trial the same way, so neither
 * decision is duplicated. Anything other than a refused trial is thrown for the
 * caller to surface.
 */
export async function startCheckout(options: {
  plan: Exclude<Plan, "free">;
  interval?: BillingInterval;
  /** Route the user comes back to, e.g. "/dashboard". */
  returnPath: string;
  allowTrial?: boolean;
}): Promise<CheckoutStartResult> {
  // A new checkout supersedes any card round trip the tab abandoned, so its
  // return is never mistaken for one.
  takePendingPlanChange();
  try {
    const { url } = await api.checkout.createSession({
      plan: options.plan,
      interval: options.interval,
      allowTrial: options.allowTrial,
      ...checkoutReturnUrls(options.returnPath),
    });

    window.location.href = url;
    return { status: "redirecting" };
  } catch (err) {
    if (err instanceof ApiError && err.code === "trial_not_eligible") {
      return { status: "trial_not_eligible", message: err.message };
    }
    throw err;
  }
}

/**
 * The plan a trial was changing to when it left to add a card (task 266).
 *
 * A no-card trial cannot change plans, and adding the card is a round trip
 * through Stripe. Kept in sessionStorage so the billing page can reopen the
 * plan-change confirmation on the way back: the customer confirms the charge
 * with the card in place, rather than being charged by the return itself.
 * A lost value costs only that convenience, so every access is guarded.
 *
 * It is stamped with when it was saved and ignored after an hour, because a
 * customer can leave Stripe by the back button and never return to clear it.
 * A stale value would otherwise reopen an old plan change on some later,
 * unrelated return from checkout.
 */
const PENDING_PLAN_CHANGE_KEY = "headerhawk.pendingPlanChange";
const PENDING_PLAN_CHANGE_TTL_MS = 60 * 60 * 1000;

interface StoredPlanChange {
  plan: string;
  interval?: string;
  savedAt: number;
}

/** The plan change a card was added for: a plan, and the interval chosen. */
export interface CardPlanChange {
  plan: Exclude<Plan, "free">;
  /** Absent when the change kept the interval (task 412). */
  interval?: BillingInterval;
}

/**
 * Send the user to Stripe to add a card to their current subscription,
 * remembering the plan change it is for. Errors are thrown for the caller.
 */
export async function startPaymentMethodSetup(options: {
  plan: Exclude<Plan, "free">;
  interval?: BillingInterval;
  /** Route the user comes back to, e.g. "/dashboard/settings/billing". */
  returnPath: string;
}): Promise<void> {
  const { url } = await api.checkout.createPaymentMethodSession(
    checkoutReturnUrls(options.returnPath),
  );
  try {
    const pending: StoredPlanChange = {
      plan: options.plan,
      ...(options.interval && { interval: options.interval }),
      savedAt: Date.now(),
    };
    sessionStorage.setItem(PENDING_PLAN_CHANGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage is unavailable; the customer picks the plan again on return.
  }
  window.location.href = url;
}

/** Read and forget the plan change a card was added for, if any. */
export function takePendingPlanChange(): CardPlanChange | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PLAN_CHANGE_KEY);
    sessionStorage.removeItem(PENDING_PLAN_CHANGE_KEY);
    if (raw === null) return null;
    const pending = JSON.parse(raw) as Partial<StoredPlanChange>;
    const fresh =
      typeof pending.savedAt === "number" &&
      Date.now() - pending.savedAt < PENDING_PLAN_CHANGE_TTL_MS;
    if (
      !fresh ||
      typeof pending.plan !== "string" ||
      !isSelfServePlan(pending.plan)
    ) {
      return null;
    }
    return {
      plan: pending.plan as Exclude<Plan, "free">,
      ...(isBillingInterval(pending.interval) && {
        interval: pending.interval,
      }),
    };
  } catch {
    return null;
  }
}

interface UseCheckoutReturnOptions {
  /**
   * Called once the API has confirmed a paid, active-or-trialing subscription.
   */
  onVerified: (
    plan: Exclude<Plan, "free">,
    subscription: CheckoutSubscriptionInfo,
  ) => void;
  /** Called when the user came back via the cancel URL. Defaults to a toast. */
  onCancelled?: () => void;
}

interface UseCheckoutReturnResult {
  /** True while a verification request is in flight. */
  isVerifying: boolean;
  /**
   * True when this page load is a return from Stripe that has not resolved yet.
   *
   * Distinct from `isVerifying`: it is true on the very first render, before
   * the effect fires, so a page can avoid flashing its pre-checkout state for a
   * frame on the way back.
   */
  isReturning: boolean;
}

export function useCheckoutReturn({
  onVerified,
  onCancelled,
}: UseCheckoutReturnOptions): UseCheckoutReturnResult {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);

  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled") === "true";
  // `upgraded=true` is the parameter the billing page used to trust on its own.
  // Still recognised so an in-flight redirect or an old bookmark does not leave
  // a stray parameter in the URL - but it grants nothing.
  const legacyUpgraded = searchParams.get("upgraded") === "true";

  // Callers pass inline closures over their own state, so these identities
  // change on every render. Held in refs and read at call time: the effect must
  // depend on the session ID, not on how often the page re-renders.
  const callbacks = useRef({ onVerified, onCancelled });
  callbacks.current = { onVerified, onCancelled };

  // A verification is not idempotent from the user's side - it toasts, and in
  // onboarding it navigates - so each session ID is handled exactly once, even
  // if React re-runs the effect (StrictMode double-invokes it in development).
  const handled = useRef<string | null>(null);

  useEffect(() => {
    const setParams = () => setSearchParams({}, { replace: true });

    if (cancelled) {
      if (handled.current === "cancelled") return;
      handled.current = "cancelled";

      if (callbacks.current.onCancelled) {
        callbacks.current.onCancelled();
      } else {
        toast.info(
          "Payment cancelled. You can select a different plan or try again.",
        );
      }
      setParams();
      return;
    }

    if (sessionId) {
      if (handled.current === sessionId) return;
      handled.current = sessionId;

      setIsVerifying(true);
      void (async () => {
        try {
          const { plan, subscription } = await api.checkout.verify(sessionId);

          if (
            subscription &&
            (subscription.status === "active" ||
              subscription.status === "trialing") &&
            plan !== "free"
          ) {
            callbacks.current.onVerified(plan, subscription);
          } else {
            toast.error(
              "Could not confirm your subscription. Please refresh the page or contact support.",
            );
          }
        } catch (err) {
          const message =
            err instanceof Error ? err.message : "Failed to verify checkout";
          toast.error(message);
        } finally {
          setParams();
          setIsVerifying(false);
        }
      })();
      return;
    }

    if (legacyUpgraded) {
      // Deliberately silent. Announcing success here without asking the API is
      // the exact defect this hook replaces.
      setParams();
    }
  }, [sessionId, cancelled, legacyUpgraded, setSearchParams]);

  return {
    isVerifying,
    isReturning: isVerifying || sessionId !== null,
  };
}
